import { useEffect } from "react";
import { z } from "zod";
import {
  Box,
  useIsTouchDevice,
  WidgetEmpty,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { SanitizedHtml } from "#app/components/editor/sanitized-html";
import { useEditorAutosave } from "#app/components/editor/use-editor-autosave";
import { isRichTextEmpty } from "#app/lib/utils/rich-text-value";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { resolveStoredTitle } from "../shared/derived-titles";

/**
 * `note` — a free-form WYSIWYG note card. Unlike the chart cards this one is
 * PRESENTATIONAL (no data hook, no `.graphql`) and INLINE-EDITABLE: it renders the
 * app's existing TipTap editor (`RichTextEditor`, compact) directly in the card body
 * and persists the HTML into the instance's own `config.content` blob via the
 * body-level `onConfigChange` seam. Any number of notes can live on one board — each
 * is a distinct widget instance with its own id + config. Viewers without
 * `widgets.manage` get the read-only `SanitizedHtml` render (same XSS-safe path as
 * issue notes), which is also our defense-in-depth since widget config is stored
 * schema-on-read (unsanitized) server-side.
 */
// `title` is written into config by WidgetCard's built-in rename. A loose object
// (plus updateConfig's merge) keeps it intact when we patch `content`; a plain
// object would strip it.
export const noteConfigSchema = z.looseObject({
  // The note body as sanitized HTML (the format `RichTextEditor` emits/consumes).
  content: z.string().default(""),
});

export type NoteConfig = z.infer<typeof noteConfigSchema>;

function NoteRender({ config, onConfigChange }: WidgetRenderContext<NoteConfig>) {
  // `onConfigChange` IS the edit-rights signal: the host wires it only when the
  // viewer may edit the board they are looking at (My Work's owner, a shared
  // board's writer, any viewer in personal mode — whose note edit forks like
  // any other change) and omits it in read-only contexts (the settings-dialog
  // preview). No separate `widgets.manage` check — that double-gate locked My
  // Work's owner, who needs no permission on their own private view, out of
  // editing their own note.
  const editable = !!onConfigChange;
  // The block grip only answers a hovering pointer, so a touch screen keeps
  // the card's full width for text instead of an empty gutter.
  const touch = useIsTouchDevice();

  // Autosave: the editor holds the live document; we flush HTML on a debounce and,
  // critically, on unmount (card removed / navigated away). useWidgetLayout adds its
  // own 500ms debounce + unmount flush on top, so an edit can't be stranded.
  const { onUpdate, setInitialHtml } = useEditorAutosave(async (html) => {
    onConfigChange?.({ content: html });
  });
  // Seed the "saved" baseline once from the persisted content so we never write on
  // mount and only ever flush genuine edits.
  useEffect(() => {
    setInitialHtml(config.content);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only baseline seed
  }, [setInitialHtml]);

  if (!editable) {
    return isRichTextEmpty(config.content) ? (
      <WidgetEmpty title="Empty note" />
    ) : (
      <SanitizedHtml html={config.content} />
    );
  }

  // No visible toolbar (the floating one still appears over a selection);
  // block formatting is reached through the "/" menu, which the placeholder
  // advertises. The same editor as a project's Description: a grip beside the
  // hovered block drags it, an empty line's "+" opens the "/" menu, and @/#
  // mention entities.
  // The grip stands 24px left of the text and the card's scroller clips
  // anything past its edge, so the text steps in by that gutter and the grip
  // lands on the card's content column, under the title.
  // Uncontrolled after mount: TipTap treats `content` as an initial seed and ignores
  // later prop changes, so the keystroke → onConfigChange → re-render round-trip never
  // resets the cursor. Do NOT add a changing `key` here (would remount + drop edits).
  return (
    <Box paddingLeft={touch ? undefined : "lg"}>
      <RichTextEditor
        blockHandles
        compact
        content={config.content}
        label="Note"
        mentions
        onUpdate={onUpdate}
        placeholder="Write a note"
        shortcutHints
      />
    </Box>
  );
}

export const noteDef: WidgetModule<NoteConfig> = {
  type: "note",
  title: "Note",
  // Every stored title is the user's (no derived family here); an untitled
  // blank-card placement reads as the "Note" card it came from.
  dynamicTitle: (config) => resolveStoredTitle(config, () => false, "Note", "Note"),
  icon: "file-text",
  span: { minW: 2, minH: 3, maxW: 12, maxH: 12, defaultW: 4 },
  configSchema: noteConfigSchema,
  catalog: [
    {
      key: "note.default",
      label: "Note",
      description: "A free-form rich-text note. Add as many as you like.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label sits outside every
      // derived-title family, so it sticks like a rename until the user
      // renames the card.
      tags: ["external"] satisfies readonly WidgetTag[],
      defaultConfig: { content: "" },
      defaultW: 4,
    },
  ],
  render: NoteRender,
  // No `settings`/`panel`: content is edited inline in the card body and the title
  // via the card's built-in rename. The gear dialog still opens (title + delete).
};
